import React from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { SignupApiError } from 'utils/auth/signupApi';
import { EmailErrorDiv, EmailError } from './SignUpForm.styles';

type SetFieldError = (field: string, message: any) => void;

interface UseSignupErrorHandlersArgs {
  addToast: (toast: { toastType: 'Error'; message: string }) => void;
  setSignupError: (message: string | null) => void;
  signInErrorLink: (userEmail?: string) => JSX.Element;
}

export const useSignupErrorHandlers = ({
  addToast,
  setSignupError,
  signInErrorLink,
}: UseSignupErrorHandlersArgs) => {
  const { t } = useTranslation();

  const handleAuth0Error = (
    err: any,
    setFieldError: SetFieldError,
    userEmail?: string
  ) => {
    Sentry.addBreadcrumb({
      message: 'Auth0 Signup API Call Failed',
      level: 'error',
      data: err,
    });

    const genericErrorToast = () =>
      addToast({
        toastType: 'Error',
        message: t('user.signUp.genericError'),
      });

    if (err?.statusCode !== 400) {
      genericErrorToast();
      if (err?.statusCode === 429) {
        Sentry.captureMessage('Signup Failed - (Auth0) Too many requests', {
          level: 'fatal',
          extra: {
            runbook_url:
              'https://resideo.atlassian.net/wiki/spaces/TAB/pages/5340921998/Signup+Failed+-+Auth0+Too+many+requests',
          },
        });
      } else {
        Sentry.captureMessage('Signup Failed - (Auth0) Reason Unknown', {
          level: 'fatal',
          extra: {
            runbook_url:
              'https://resideo.atlassian.net/wiki/spaces/TAB/pages/5340921969/Signup+Failed+-+Auth0+Reason+Unknown',
          },
        });
      }
      return;
    }

    if (err?.code === 'pre_user_registration_validation_error') {
      genericErrorToast();
      Sentry.captureMessage(
        'Signup Failed - (Auth0) Pre-user registration validation error',
        'fatal'
      );
      return;
    }

    if (err?.description.includes('invalid user_metadata')) {
      genericErrorToast();
      Sentry.captureMessage(
        'Signup Failed - (Auth0) Invalid user_metadata',
        'fatal'
      );
      return;
    }

    if (err?.description.includes('error in email')) {
      setFieldError(
        'email',
        <EmailErrorDiv>
          <EmailError>{t('user.signUp.invalidEmail')}</EmailError>
        </EmailErrorDiv>
      );
      return;
    }

    if (
      err?.code === 'invalid_password' &&
      err?.name === 'PasswordDictionaryError'
    ) {
      setFieldError('password', t('user.signUp.passwordTooCommon'));
      return;
    }

    setFieldError(
      'email',
      <EmailErrorDiv>
        <EmailError>{t('user.signUp.emailError')}</EmailError>
        {signInErrorLink(userEmail)}
      </EmailErrorDiv>
    );
  };

  // 400 VALIDATION_FAILED: surface field-level errors where possible, otherwise
  // fall back to a generic toast.
  const handleRisValidationError = (
    err: SignupApiError,
    setFieldError: SetFieldError
  ) => {
    const matches = (
      detail: { message?: string; reasonCode?: string },
      re: RegExp
    ) => re.test(detail.message || '') || re.test(detail.reasonCode || '');

    const hasPasswordError = err.errors.some(e => matches(e, /password/i));
    const hasEmailError = err.errors.some(e => matches(e, /email/i));

    if (hasPasswordError) {
      setFieldError('password', t('user.signUp.passwordTooCommon'));
    }
    if (hasEmailError) {
      setFieldError(
        'email',
        <EmailErrorDiv>
          <EmailError>{t('user.signUp.invalidEmail')}</EmailError>
        </EmailErrorDiv>
      );
    }
    if (!hasPasswordError && !hasEmailError) {
      setSignupError(t('user.signUp.genericError'));
    }
  };

  const handleRisError = (
    err: unknown,
    setFieldError: SetFieldError,
    userEmail?: string
  ) => {
    Sentry.addBreadcrumb({
      message: 'RIS Signup API Call Failed',
      level: 'error',
      // Never log PII or the reCAPTCHA token; only the failure category.
      data: { kind: err instanceof SignupApiError ? err.kind : 'unknown' },
    });

    const showGenericError = () =>
      setSignupError(t('user.signUp.genericError'));

    if (!(err instanceof SignupApiError)) {
      showGenericError();
      return;
    }

    switch (err.kind) {
      case 'validation':
        handleRisValidationError(err, setFieldError);
        return;
      case 'recaptcha':
        // Generic message only; never reveal reCAPTCHA specifics to the user.
        setSignupError(t('user.signUp.recaptchaError'));
        return;
      case 'conflict':
        // 409 signals the email is already registered. Match the existing
        // production behavior: show the "account already exists" message near
        // the email field with a sign-in link so the user can recover.
        setFieldError(
          'email',
          <EmailErrorDiv>
            <EmailError>{t('user.signUp.emailError')}</EmailError>
            {signInErrorLink(userEmail)}
          </EmailErrorDiv>
        );
        return;
      case 'unavailable':
        // 503 masks transient backend outages. Show a generic inline error
        // only — never attach anything to the email field. The reCAPTCHA
        // widget below the form is reset by the caller so the user can retry
        // with a fresh token.
        showGenericError();
        return;
      case 'network':
      default:
        showGenericError();
    }
  };

  return { handleAuth0Error, handleRisError };
};
